import { generateValues } from 'dashboard/helper/filterQueryGenerator';

/**
 * Avaliação client-side dos filtros avançados de conversa.
 *
 * Quando um filtro está aplicado (ou uma pasta está ativa) a lista deixa de ser
 * montada pelos getters de aba e passa a exibir tudo que está em
 * `allConversations`. Por isso qualquer conversa injetada no store por evento de
 * websocket aparece na listagem, mesmo sem casar com o filtro (issue #1330).
 *
 * O matcher reproduz a semântica do backend (`FilterService` + `FilterHelper`):
 *
 * - precedência: o `query_operator` de cada item conecta o item ao SEGUINTE e o
 *   backend concatena tudo numa única string SQL, então `AND` liga mais forte
 *   que `OR` (`a OR b AND c` == `a OR (b AND c)`);
 * - `equal_to`/`not_equal_to` viram `IN`/`NOT IN`: com valor NULL o Postgres
 *   devolve NULL e a linha NÃO entra no resultado — inclusive no `NOT IN`;
 * - `labels` usa `EXISTS`/`NOT EXISTS`, então não tem a armadilha do NULL;
 * - atributos de data são comparados como `date` (`::date`), não como timestamp.
 *
 * Nem tudo é decidível no cliente: o payload de websocket
 * (`Conversations::EventDataPresenter`) não carrega `campaign_id` nem
 * `last_non_activity_message_direction`. Nesses casos devolvemos UNKNOWN e quem
 * chama decide — a política adotada é conservadora: só adiciona na lista o que
 * casa comprovadamente (MATCHED), e só remove o que comprovadamente não casa
 * (NOT_MATCHED).
 */
export const FILTER_MATCH = {
  MATCHED: 'matched',
  NOT_MATCHED: 'not_matched',
  UNKNOWN: 'unknown',
};

const DATE_ATTRIBUTES = ['created_at', 'last_activity_at'];

// Atributos padrão da conversa presentes no payload de websocket.
const STANDARD_ATTRIBUTES = {
  status: conversation => conversation.status,
  assignee_id: conversation => conversation.meta?.assignee?.id,
  inbox_id: conversation => conversation.inbox_id,
  team_id: conversation => conversation.meta?.team?.id,
  // No payload do dashboard `id` já é o display_id da conversa.
  display_id: conversation => conversation.id,
  priority: conversation => conversation.priority,
  created_at: conversation => conversation.created_at,
  last_activity_at: conversation => conversation.last_activity_at,
};

// Chaves lidas de `conversations.additional_attributes ->> key`.
const ADDITIONAL_ATTRIBUTES = [
  'browser_language',
  'conversation_language',
  'country_code',
  'referer',
  'mail_subject',
];

// Chaves suportadas pelo backend que o payload de websocket não carrega.
const UNAVAILABLE_ATTRIBUTES = [
  'campaign_id',
  'last_non_activity_message_direction',
];

// SQL NULL: so a ausencia do valor. String vazia e um valor como outro
// qualquer para o Postgres -- `'' IS NOT NULL` e verdadeiro, `'' NOT IN (...)`
// tambem. Tratar `''` como ausente faria, por exemplo, um filtro
// `is_not_present` casar com uma conversa que o backend deixa de fora.
const isMissing = value => value === null || value === undefined;

const isBlank = value => isMissing(value) || value === '';

const toBoolean = value => {
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (['true', 't', '1'].includes(text)) return true;
  if (['false', 'f', '0'].includes(text)) return false;
  return null;
};

// `(coluna)::date > 'YYYY-MM-DD'::date` — comparação por dia, em UTC.
const toEpochDay = value => {
  if (isBlank(value)) return null;
  // Timestamps do payload vêm em segundos.
  if (typeof value === 'number') {
    return Math.floor(value / 86400);
  }
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && /^\d+$/.test(String(value).trim())) {
    return Math.floor(asNumber / 86400);
  }
  const parsed = Date.parse(`${value}T00:00:00Z`);
  const fallback = Number.isNaN(parsed) ? Date.parse(value) : parsed;
  if (Number.isNaN(fallback)) return null;
  return Math.floor(fallback / 86400000);
};

// Como os dois lados sao comparados depois do cast que o backend aplica.
// Devolvem `null` quando o valor nao cabe no tipo -- ali o Postgres levantaria
// erro no cast e nao ha resultado a espelhar.
//
// `strict` e o caso das colunas e dos `additional_attributes`: `IN (:values)`
// compara byte a byte, sem LOWER nenhum. `insensitive` cobre os atributos
// customizados de texto, onde `build_custom_attr_query` envolve a coluna em
// `LOWER(...)` e `case_insensitive_values` faz o mesmo com o valor enviado.
// `Number('')` e 0, nao NaN. Para o Postgres a string vazia nao e numero:
// `''::numeric` levanta erro e a consulta inteira falha, entao nao ha
// resultado a espelhar e a comparacao fica indecidivel.
const toNumber = value => {
  if (isBlank(value)) return null;
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const COMPARATORS = {
  // Ids chegam ora como numero ora como string ('5' vs 5).
  strict: (actual, expected) => String(actual) === String(expected),
  insensitive: (actual, expected) =>
    String(actual).toLowerCase() === String(expected).toLowerCase(),
  numeric: (actual, expected) => {
    const left = toNumber(actual);
    const right = toNumber(expected);
    if (left === null || right === null) return null;
    return left === right;
  },
  boolean: (actual, expected) => {
    const left = toBoolean(actual);
    const right = toBoolean(expected);
    if (left === null || right === null) return null;
    return left === right;
  },
  date: (actual, expected) => {
    const left = toEpochDay(actual);
    const right = toEpochDay(expected);
    if (left === null || right === null) return null;
    return left === right;
  },
};

// Ordenacao por tipo, para `is_greater_than`/`is_less_than`. Tipos fora daqui
// nao suportam o operador no backend (o cast falharia).
const ORDINALS = {
  numeric: toNumber,
  date: toEpochDay,
};

// `true` quando algum valor casa, `false` quando nenhum casa e `null` quando a
// comparacao nao pode ser feita para nenhum deles.
const includesValue = (values, actual, kind) => {
  const compare = COMPARATORS[kind];
  let undecided = false;
  const matched = values.some(expected => {
    const result = compare(actual, expected);
    if (result === null) {
      undecided = true;
      return false;
    }
    return result;
  });
  if (matched) return true;
  return undecided ? null : false;
};

const todayEpochDay = () => Math.floor(Date.now() / 86400000);

const matchResult = value =>
  value ? FILTER_MATCH.MATCHED : FILTER_MATCH.NOT_MATCHED;

const evaluateLabels = (conversation, operator, values) => {
  const labels = (conversation.labels || []).map(label => label.title || label);

  switch (operator) {
    // `tags.name IN (:values)`: sem LOWER, portanto sensivel a caixa.
    case 'equal_to':
      return matchResult(
        values.some(value => includesValue(labels, value, 'strict') === true)
      );
    case 'not_equal_to':
      return matchResult(
        !values.some(value => includesValue(labels, value, 'strict') === true)
      );
    case 'is_present':
      return matchResult(labels.length > 0);
    case 'is_not_present':
      return matchResult(labels.length === 0);
    default:
      return FILTER_MATCH.UNKNOWN;
  }
};

const evaluateDate = (actual, operator, values) => {
  const actualDay = toEpochDay(actual);
  if (actualDay === null) return FILTER_MATCH.NOT_MATCHED;

  if (operator === 'days_before') {
    const days = Number(values[0]);
    if (Number.isNaN(days)) return FILTER_MATCH.UNKNOWN;
    return matchResult(actualDay < todayEpochDay() - days);
  }

  const expectedDay = toEpochDay(values[0]);
  if (expectedDay === null) return FILTER_MATCH.UNKNOWN;

  if (operator === 'is_greater_than')
    return matchResult(actualDay > expectedDay);
  if (operator === 'is_less_than') return matchResult(actualDay < expectedDay);
  return FILTER_MATCH.UNKNOWN;
};

// `nullMatchesNotEqualTo` reproduz o `OR coluna IS NULL` que o backend adiciona
// ao `not_equal_to` de atributo customizado (e que não existe nas colunas
// padrão, onde `NULL NOT IN (...)` descarta a linha).
const evaluateScalar = (
  actual,
  operator,
  values,
  { nullMatchesNotEqualTo, kind = 'strict' }
) => {
  switch (operator) {
    case 'is_present':
      return matchResult(!isMissing(actual));
    case 'is_not_present':
      return matchResult(isMissing(actual));
    case 'equal_to': {
      if (isMissing(actual)) return FILTER_MATCH.NOT_MATCHED;
      const included = includesValue(values, actual, kind);
      if (included === null) return FILTER_MATCH.UNKNOWN;
      return matchResult(included);
    }
    case 'not_equal_to': {
      if (isMissing(actual)) return matchResult(nullMatchesNotEqualTo);
      const included = includesValue(values, actual, kind);
      if (included === null) return FILTER_MATCH.UNKNOWN;
      return matchResult(!included);
    }
    case 'contains':
      // `'' ILIKE '%x%'` e falso; so o NULL some do resultado por ser NULL.
      if (isMissing(actual)) return FILTER_MATCH.NOT_MATCHED;
      return matchResult(
        values.some(value =>
          String(actual).toLowerCase().includes(String(value).toLowerCase())
        )
      );
    case 'does_not_contain':
      // Ja `'' NOT ILIKE ALL (...)` e verdadeiro: string vazia casa.
      if (isMissing(actual)) return FILTER_MATCH.NOT_MATCHED;
      return matchResult(
        !values.some(value =>
          String(actual).toLowerCase().includes(String(value).toLowerCase())
        )
      );
    case 'is_greater_than':
    case 'is_less_than': {
      // `Number(null)` e 0, nao NaN: sem este guard o valor ausente seria
      // comparado como zero, enquanto `NULL < :valor` devolve NULL e a linha
      // fica fora do resultado.
      if (isMissing(actual)) return FILTER_MATCH.NOT_MATCHED;
      const toOrdinal = ORDINALS[kind];
      if (!toOrdinal) return FILTER_MATCH.UNKNOWN;
      const left = toOrdinal(actual);
      const right = toOrdinal(values[0]);
      if (left === null || right === null) return FILTER_MATCH.UNKNOWN;
      return matchResult(
        operator === 'is_greater_than' ? left > right : left < right
      );
    }
    default:
      return FILTER_MATCH.UNKNOWN;
  }
};

// `generateValues` assume o formato produzido pela UI; um payload inesperado
// (ex.: `values: null`) não pode derrubar o handler de websocket.
const safeGenerateValues = filter => {
  try {
    const values = generateValues(filter);
    return Array.isArray(values) ? values : [];
  } catch (error) {
    return [];
  }
};

// `attribute_display_type` -> como o backend converte os dois lados
// (`FilterService::ATTRIBUTE_TYPES`). `text`, `link` e `list` viram `::text`
// com LOWER; `number` vira `::numeric`; `checkbox`, `::boolean`; `date`,
// `::date`.
const CUSTOM_ATTRIBUTE_KINDS = {
  text: 'insensitive',
  link: 'insensitive',
  list: 'insensitive',
  number: 'numeric',
  checkbox: 'boolean',
  date: 'date',
};

export const evaluateFilter = (
  conversation,
  filter,
  customAttributeTypes = {}
) => {
  const key = filter.attribute_key;
  const operator = filter.filter_operator;

  if (!key || !operator) return FILTER_MATCH.UNKNOWN;
  if (UNAVAILABLE_ATTRIBUTES.includes(key)) return FILTER_MATCH.UNKNOWN;

  let values = [];
  if (!['is_present', 'is_not_present'].includes(operator)) {
    values = safeGenerateValues(filter);
    // Filtro sem valor é rejeitado pelo backend; não temos como avaliar.
    if (!values.length) return FILTER_MATCH.UNKNOWN;
  }

  if (key === 'labels') {
    return evaluateLabels(conversation, operator, values);
  }

  if (DATE_ATTRIBUTES.includes(key)) {
    return evaluateDate(
      STANDARD_ATTRIBUTES[key](conversation),
      operator,
      values
    );
  }

  if (STANDARD_ATTRIBUTES[key]) {
    return evaluateScalar(
      STANDARD_ATTRIBUTES[key](conversation),
      operator,
      values,
      { nullMatchesNotEqualTo: false, kind: 'strict' }
    );
  }

  if (ADDITIONAL_ATTRIBUTES.includes(key)) {
    return evaluateScalar(
      conversation.additional_attributes?.[key],
      operator,
      values,
      { nullMatchesNotEqualTo: false, kind: 'strict' }
    );
  }

  // Qualquer outra chave é tratada pelo backend como custom attribute da
  // conversa (`handle_nil_filter`), lido de `conversations.custom_attributes`.
  // O backend converte os dois lados conforme o `attribute_display_type` do
  // atributo, entao `equal_to '3.0'` casa com o valor 3 num atributo numerico.
  // Sem conhecer o tipo nao da para espelhar isso -- melhor declarar
  // indecidivel do que comparar como texto.
  const kind = CUSTOM_ATTRIBUTE_KINDS[customAttributeTypes[key]];
  if (!kind) return FILTER_MATCH.UNKNOWN;

  return evaluateScalar(
    conversation.custom_attributes?.[key],
    operator,
    values,
    {
      // `not_equal_to` de atributo customizado leva um `OR ... IS NULL` no
      // backend, entao o valor ausente casa.
      nullMatchesNotEqualTo: true,
      kind,
    }
  );
};

// `a AND b OR c` => [[a, b], [c]]: AND liga mais forte que OR, igual ao SQL.
const groupByQueryOperator = filters =>
  filters.reduce(
    (groups, filter, index) => {
      groups[groups.length - 1].push(filter);
      const operator = (filter.query_operator || 'and')
        .toString()
        .toLowerCase();
      if (operator === 'or' && index < filters.length - 1) {
        groups.push([]);
      }
      return groups;
    },
    [[]]
  );

export const matchesAppliedFilters = (
  conversation,
  filters = [],
  customAttributeTypes = {}
) => {
  if (!conversation || !filters.length) return FILTER_MATCH.UNKNOWN;

  const groupResults = groupByQueryOperator(filters).map(group => {
    const results = group.map(filter =>
      evaluateFilter(conversation, filter, customAttributeTypes)
    );
    if (results.includes(FILTER_MATCH.NOT_MATCHED))
      return FILTER_MATCH.NOT_MATCHED;
    if (results.includes(FILTER_MATCH.UNKNOWN)) return FILTER_MATCH.UNKNOWN;
    return FILTER_MATCH.MATCHED;
  });

  if (groupResults.includes(FILTER_MATCH.MATCHED)) return FILTER_MATCH.MATCHED;
  if (groupResults.includes(FILTER_MATCH.UNKNOWN)) return FILTER_MATCH.UNKNOWN;
  return FILTER_MATCH.NOT_MATCHED;
};
